import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {APPROVAL_TRACKER_DECISION_MODEL, getToastMessageByStatus} from "../Model/Model";
import {toast} from "react-toastify";
import {errorMsg} from "../../../../Apis/functions";
import {useDispatch, useSelector} from "react-redux";
import {STATUS_MODEL} from "../../../../enums/statusEnum";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * GeneralCommentModalApp Hook
 *
 * This custom hook manages the logic for the general comment modal in an approval tracker flow.
 * It handles user input for approval/denial comments, form validation, submission via API,
 * and provides UI feedback through toasts and dynamic messages.
 *
 * @param {Object} props
 * @param {string|number} props.row - The approval level ID to which the decision applies.
 * @param {Function} props.setModified - Callback to notify the parent component that data has changed.
 * @param {string} props.status - The status being applied (e.g., "APPROVED", "DENIED", "CHANGES_REQUESTED").
 * @param {Function} props.onHide - Callback to close or hide the modal after submission.
 * @param {string} props.module - The module name (e.g., "EVENT", "FOODIE") used for routing and context.
 * @param {string} props.endPoint - Dynamic endpoint used for API submission.
 *
 * @returns {Object} - An object containing state and handlers for modal behavior and form management.
 */

export const GeneralCommentModalApp = ({row,setModified,status,onHide,module,endPoint}) =>{

    const [emptyFields, setEmptyFields] = useState(false);
    const [data, setData] = useState({...APPROVAL_TRACKER_DECISION_MODEL});
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { getApprovalTrackerProgress } = useSelector((state) => state.ApprovalTrackerSlice);
    const [infoParagraph, setInfoParagraph] = useState('');

   const handleChange = (e) => {
        const { name, value } = e.target;
        setData(prev => ({
            ...prev,
            [name]: value
        }));
    }; 

    const resetValues = () =>{
        setData(prev => ({
            ...prev,
            reason: ''
        }));
    }

    const handleCreate = (approvalStatusId) => {
        approvalStatusId = approvalStatusId ?? STATUS_MODEL.PENDING_REVIEW;
        const aux = {
            ...data,
            approvalFlowId: getApprovalTrackerProgress?.approvalFlowId,
            approvalLevelId: row,
            approvalStatusId: approvalStatusId,
            module: module,
            subjectId: getApprovalTrackerProgress?.moduleId,
        };

        if (status !== STATUS_MODEL.APPROVED && !data?.isValid()) {
            setEmptyFields(true);
            return;
        }

        const body = aux.toRegister();

        dispatch(endPoint(body)).then(
            ({ payload: { data: { success, code } } }) => {
                if (success) {
                    resetValues();
                    toast.success(t(getToastMessageByStatus(approvalStatusId )));
                    setModified(true)
                    onHide()
                } else {
                    errorMsg(code);
                }
            }
        );

        setEmptyFields(false);
    };

    useEffect(() => {
        switch (status) {
            case STATUS_MODEL.APPROVED:
                setInfoParagraph('you_can_leave_a_optional_comment');
                break;
            case STATUS_MODEL.DENIED:
                setInfoParagraph('please_provide_a_reason');
                break;
            case STATUS_MODEL.CHANGES_REQUESTED:
                setInfoParagraph('please_described_changes');
                break;
            default:
                setInfoParagraph(STATUS_MODEL.PENDING_REVIEW);
                break;
        }
    }, [row, status])

    return {
        resetValues,
        emptyFields,
        handleChange,
        t,
        handleCreate,
        data,
        infoParagraph
    }
}
